import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { AccountKind, CategoryKind, DocumentType } from "@xynex-money/shared";
import type { GmailRule } from "@xynex-money/shared";
import { useAuth } from "../auth";
import { api, ApiError } from "../api";
import { connectGmail } from "../native/gmailOAuth";
import { useCrudResource } from "../hooks/useCrudResource";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { fmtDate, fmtINR } from "../fmt";

const ACCOUNT_KINDS = AccountKind.options;
const CATEGORY_KINDS = CategoryKind.options;
const DOC_TYPES_FOR_GMAIL = DocumentType.options;

interface Account {
  id: string;
  kind: AccountKind;
  name: string;
  institution: string | null;
  numberMask: string | null;
  isActive: boolean;
}
interface Category {
  id: string;
  name: string;
  kind: CategoryKind;
  parentId: string | null;
}
interface GmailStatus {
  connected: boolean;
  email: string | null;
  last_sync: string | null;
  rules: GmailRule[];
}
interface Party {
  id: string;
  name: string;
}
interface CategorizeRule {
  id: string;
  matchKind: "contains" | "regex";
  pattern: string;
  setPartyId: string | null;
  setCategoryId: string | null;
  priority: number;
}

function AccountsSection() {
  const { items: accounts, loading, error, setError, create, update, remove } = useCrudResource<Account>("/api/accounts");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [form, setForm] = useState({
    kind: "bank" as AccountKind,
    name: "",
    institution: "",
    numberMask: "",
    isActive: true,
  });

  const openAdd = () => {
    setEditing(null);
    setForm({ kind: "bank", name: "", institution: "", numberMask: "", isActive: true });
    setModalOpen(true);
  };
  const openEdit = (a: Account) => {
    setEditing(a);
    setForm({ kind: a.kind, name: a.name, institution: a.institution ?? "", numberMask: a.numberMask ?? "", isActive: a.isActive });
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const body = {
      kind: form.kind,
      name: form.name,
      institution: form.institution || null,
      numberMask: form.numberMask || null,
      isActive: form.isActive,
    };
    try {
      if (editing) await update(editing.id, body);
      else await create(body);
      setModalOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (a: Account) => {
    if (!confirm(`Delete account "${a.name}"?`)) return;
    try {
      await remove(a.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed (it may have transactions linked)");
    }
  };

  const columns: Column<Account>[] = [
    { key: "name", header: "Name" },
    { key: "kind", header: "Kind" },
    { key: "institution", header: "Institution", render: (a) => a.institution ?? "-" },
    { key: "isActive", header: "Active", render: (a) => (a.isActive ? "Yes" : "No") },
    {
      key: "actions",
      header: "",
      render: (a) => (
        <span className="actions-inline">
          <button type="button" onClick={() => openEdit(a)}>
            Edit
          </button>
          <button type="button" className="danger" onClick={() => onDelete(a)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  return (
    <section>
      <h2>Accounts</h2>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>
          Add account
        </button>
      </div>
      <Table columns={columns} rows={accounts} rowKey={(a) => a.id} emptyText={loading ? "Loading…" : "No accounts yet."} />

      {modalOpen && (
        <Modal title={editing ? "Edit account" : "Add account"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="acc-kind">Kind</label>
                <select id="acc-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as Account["kind"] })}>
                  {ACCOUNT_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="acc-name">Name</label>
                <input id="acc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="acc-inst">Institution</label>
                <input id="acc-inst" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="acc-mask">Number mask</label>
                <input id="acc-mask" placeholder="xxxx1234" value={form.numberMask} onChange={(e) => setForm({ ...form, numberMask: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                Active
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

function CategoriesSection() {
  const { items: categories, loading, error, setError, create, remove } = useCrudResource<Category>("/api/categories");
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CategoryKind>("expense");

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create({ name, kind });
      setModalOpen(false);
      setName("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (c: Category) => {
    if (!confirm(`Delete category "${c.name}"?`)) return;
    try {
      await remove(c.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const columns: Column<Category>[] = [
    { key: "name", header: "Name" },
    { key: "kind", header: "Kind" },
    {
      key: "actions",
      header: "",
      render: (c) => (
        <button type="button" className="danger" onClick={() => onDelete(c)}>
          Delete
        </button>
      ),
    },
  ];

  return (
    <section>
      <h2>Categories</h2>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={() => setModalOpen(true)}>
          Add category
        </button>
      </div>
      <Table columns={columns} rows={categories} rowKey={(c) => c.id} emptyText={loading ? "Loading…" : "No categories yet."} />

      {modalOpen && (
        <Modal title="Add category" onClose={() => setModalOpen(false)}>
          <form onSubmit={onAdd} className="modal-body">
            <div className="field">
              <label htmlFor="cat-name">Name</label>
              <input id="cat-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="cat-kind">Kind</label>
              <select id="cat-kind" value={kind} onChange={(e) => setKind(e.target.value as (typeof CATEGORY_KINDS)[number])}>
                {CATEGORY_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

/**
 * Read + delete only, no add/edit UI: the only sanctioned way to create a
 * categorize rule stays the Import screen's "save as rule" checkbox
 * (POST /api/imports/:batchId/commit's saveRule flag) — see
 * apps/api/src/modules/rules-api.ts's header comment. This section is the
 * "review what's been auto-created, prune the bad ones" surface.
 */
function RulesSection() {
  const {
    items: rules,
    loading,
    error,
    setError,
    remove,
  } = useCrudResource<CategorizeRule>("/api/rules");
  const [parties, setParties] = useState<Party[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    api
      .get<Party[]>("/api/parties")
      .then(setParties)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load parties"));
    api
      .get<Category[]>("/api/categories")
      .then(setCategories)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load categories"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partyName = (id: string | null) => (id ? parties.find((p) => p.id === id)?.name ?? "-" : "-");
  const categoryName = (id: string | null) => (id ? categories.find((c) => c.id === id)?.name ?? "-" : "-");

  const onDelete = async (r: CategorizeRule) => {
    if (!confirm(`Delete rule "${r.pattern}"?`)) return;
    try {
      await remove(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const columns: Column<CategorizeRule>[] = [
    { key: "pattern", header: "Pattern" },
    { key: "matchKind", header: "Kind" },
    { key: "party", header: "Party", render: (r) => partyName(r.setPartyId) },
    { key: "category", header: "Category", render: (r) => categoryName(r.setCategoryId) },
    { key: "priority", header: "Priority", align: "right" },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <button type="button" className="danger" onClick={() => onDelete(r)}>
          Delete
        </button>
      ),
    },
  ];

  return (
    <section>
      <h2>Categorize rules</h2>
      {error && <div className="error-text">{error}</div>}
      <Table
        columns={columns}
        rows={rules}
        rowKey={(r) => r.id}
        emptyText={loading ? "Loading…" : "No rules yet — save one from the Import screen."}
      />
    </section>
  );
}

function GmailSection() {
  const [status, setStatus] = useState<GmailStatus | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [rules, setRules] = useState<GmailRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const load = () => {
    api
      .get<GmailStatus>("/api/gmail/status")
      .then((s) => {
        setStatus(s);
        setRules(s.rules ?? []);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load gmail status"));
  };
  useEffect(load, []);
  useEffect(() => {
    api
      .get<Account[]>("/api/accounts")
      .then(setAccounts)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load accounts"));
  }, []);

  const onConnect = async () => {
    try {
      // Branches web (full-page nav) vs native (Custom Tab + deep-link return).
      await connectGmail();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not start gmail connect");
    }
  };

  const addRule = () => setRules((prev) => [...prev, { query: "", doc_type: "other" }]);
  const updateRule = (i: number, patch: Partial<GmailRule>) =>
    setRules((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRule = (i: number) => setRules((prev) => prev.filter((_, idx) => idx !== i));

  // Best-effort starter rules for the common Indian brokers/registrars. Queries
  // target their sender + an Excel/CSV attachment; the user edits/verifies each
  // (only .xlsx/.csv CG statements auto-import — most brokers don't email one).
  // Card/bank statement issuers (same sender queries the standalone Gmail
  // downloader uses). Fetched PDFs are vaulted as card_stmt documents; assign
  // an account per rule afterwards so parsed rows import to the right account.
  const addCardStatementPresets = () => {
    const issuers = [
      "sbicard.com",
      "hdfcbank.net",
      "icicibank.com",
      "axisbank.com",
      "kotak.com",
      "americanexpress.com",
      "rblbank.com",
      "idfcfirstbank.com",
      "getonecard.app",
      "aubank.in",
      "indusind.com",
    ];
    const presets: GmailRule[] = issuers.map((sender) => ({
      query: `from:${sender} has:attachment filename:pdf`,
      doc_type: "card_stmt",
    }));
    setRules((prev) => [
      ...prev,
      ...presets.filter((p) => !prev.some((r) => r.query === p.query)),
    ]);
  };

  const addBrokerCgPresets = () => {
    const presets: GmailRule[] = [
      { query: "from:icicidirect.com has:attachment (filename:xlsx OR filename:csv)", doc_type: "other", capgains_default_class: "equity" },
      { query: "from:(zerodha.net OR zerodha.com) has:attachment (filename:xlsx OR filename:csv)", doc_type: "other", capgains_default_class: "equity" },
      { query: "from:paytmmoney.com has:attachment (filename:xlsx OR filename:csv)", doc_type: "other", capgains_default_class: "equity" },
      { query: "from:camsonline.com has:attachment subject:(capital gain)", doc_type: "other", capgains_default_class: "mf" },
      { query: "from:kfintech.com has:attachment subject:(capital gain)", doc_type: "other", capgains_default_class: "mf" },
    ];
    setRules((prev) => [
      ...prev,
      ...presets.filter((p) => !prev.some((r) => r.query === p.query)),
    ]);
  };

  const saveRules = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/gmail/rules", rules);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to save rules");
    } finally {
      setBusy(false);
    }
  };

  const onSyncNow = async () => {
    setBusy(true);
    setSyncResult(null);
    setError(null);
    try {
      const res = await api.post<Record<string, unknown>>("/api/gmail/sync-now");
      setSyncResult(JSON.stringify(res));
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "sync failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2>Gmail auto-fetch</h2>
      {error && <div className="error-text">{error}</div>}
      <div className="card">
        <p>
          Status:{" "}
          {status?.connected ? (
            <strong>Connected as {status.email}</strong>
          ) : (
            <span className="muted">Not connected</span>
          )}
          {status?.last_sync && <span className="muted"> — last synced {fmtDate(status.last_sync)}</span>}
        </p>
        <div className="toolbar">
          <button type="button" onClick={onConnect}>
            {status?.connected ? "Reconnect Gmail" : "Connect Gmail"}
          </button>
          {status?.connected && (
            <button type="button" disabled={busy} onClick={onSyncNow}>
              {busy ? "Syncing..." : "Sync now"}
            </button>
          )}
        </div>
        {syncResult && <div className="muted">Sync result: {syncResult}</div>}

        {status?.connected && (
          <>
            <h2 style={{ marginTop: "1rem" }}>Rules</h2>
            <div className="modal-body">
              {rules.map((r, i) => (
                <div className="toolbar" key={i}>
                  <div className="field">
                    <label>Gmail query</label>
                    <input value={r.query} onChange={(e) => updateRule(i, { query: e.target.value })} placeholder="from:hdfc.com" />
                  </div>
                  <div className="field">
                    <label>Doc type</label>
                    <select value={r.doc_type} onChange={(e) => updateRule(i, { doc_type: e.target.value as DocumentType })}>
                      {DOC_TYPES_FOR_GMAIL.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  {(r.doc_type === "bank_stmt" || r.doc_type === "card_stmt") && (
                    <div className="field">
                      <label>Account</label>
                      <select value={r.account_id ?? ""} onChange={(e) => updateRule(i, { account_id: e.target.value || undefined })}>
                        <option value="">-- none --</option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="field">
                    <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <input
                        type="checkbox"
                        checked={r.capgains_default_class != null}
                        onChange={(e) =>
                          updateRule(i, {
                            capgains_default_class: e.target.checked ? "equity" : undefined,
                          })
                        }
                      />
                      Capital-gains
                    </label>
                  </div>
                  {r.capgains_default_class != null && (
                    <div className="field">
                      <label>Default class</label>
                      <select
                        value={r.capgains_default_class}
                        onChange={(e) =>
                          updateRule(i, {
                            capgains_default_class: e.target.value as GmailRule["capgains_default_class"],
                          })
                        }
                      >
                        {(["equity", "mf", "gold", "property", "other"] as const).map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button type="button" className="danger" onClick={() => removeRule(i)}>
                    Remove
                  </button>
                </div>
              ))}
              <div className="toolbar">
                <button type="button" onClick={addRule}>
                  Add rule
                </button>
                <button type="button" onClick={addBrokerCgPresets}>
                  Add broker CG presets
                </button>
                <button type="button" onClick={addCardStatementPresets}>
                  Add card statement presets
                </button>
                <button className="primary" type="button" disabled={busy} onClick={saveRules}>
                  Save rules
                </button>
              </div>
              {rules.some((r) => r.capgains_default_class != null) && (
                <p className="muted">
                  Capital-gains rules: any .xlsx/.csv attachment they fetch is auto-imported into
                  Capital Gains (deduped). PDF statements (CAMS/contract notes) are only saved to
                  the vault for now.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function TenantsSection() {
  const [tenantName, setTenantName] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<{ tenantId: string; userId: string; inviteToken: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<{ tenantId: string; userId: string; inviteToken: string }>(
        "/api/auth/register-tenant",
        { tenantName, email }
      );
      setResult(res);
      setTenantName("");
      setEmail("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not register tenant");
    } finally {
      setBusy(false);
    }
  };

  const inviteUrl = result ? `${window.location.origin}/accept-invite?token=${result.inviteToken}` : null;

  return (
    <section>
      <h2>Tenants (superadmin)</h2>
      {error && <div className="error-text">{error}</div>}
      <div className="card">
        <form onSubmit={onSubmit} className="toolbar">
          <div className="field">
            <label htmlFor="tn-name">Tenant / family name</label>
            <input id="tn-name" required value={tenantName} onChange={(e) => setTenantName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="tn-email">Owner email</label>
            <input id="tn-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button className="primary" type="submit" disabled={busy}>
            {busy ? "Registering..." : "Register tenant"}
          </button>
        </form>
        {result && (
          <div className="muted" style={{ marginTop: "0.75rem" }}>
            Tenant created. Share this invite link with the owner (no email is sent automatically):
            <br />
            <code>{inviteUrl}</code>
          </div>
        )}
      </div>
      <ResetLinkSection />
    </section>
  );
}

/**
 * Manual reset-link generator (calls POST /api/auth/admin-reset-password).
 * Works without SMTP: unlike forgot-password (which emails the link), this
 * hands the raw link straight back so a superadmin can relay it to a
 * locked-out owner themselves. There's no tenant/owner listing endpoint yet
 * (register-tenant only returns the one it just created), so this is a
 * plain email-lookup form rather than a per-row button in a tenant table.
 */
function ResetLinkSection() {
  const [email, setEmail] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLink(null);
    setBusy(true);
    try {
      const res = await api.post<{ resetLink: string }>("/api/auth/admin-reset-password", { email });
      setLink(res.resetLink);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not generate reset link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: "1rem" }}>
      <h2 style={{ marginTop: 0 }}>Generate reset link</h2>
      <p className="muted">
        For a tenant owner who's locked out and can't receive email -- generates a fresh
        single-use reset link you can send them directly.
      </p>
      {error && <div className="error-text">{error}</div>}
      <form onSubmit={onSubmit} className="toolbar">
        <div className="field">
          <label htmlFor="reset-email">Owner email</label>
          <input id="reset-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Generating..." : "Generate reset link"}
        </button>
      </form>
      {link && (
        <div className="field" style={{ marginTop: "0.75rem" }}>
          <label htmlFor="reset-link-out">Reset link (expires in 1 hour)</label>
          <input id="reset-link-out" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
        </div>
      )}
    </div>
  );
}

interface AiStatus {
  configured: boolean;
  provider: string;
  keyPoolSize: number;
  model: string;
  enabled: boolean;
  spendCents: number;
  capCents: number;
}

/**
 * AI & Automation consent + spend. AI is off until the tenant opts in here
 * (records who/when). Shows the month-to-date spend against the cap; the cap is
 * editable. All AI features (Ask, Review, Insights, doc-extraction) gate on
 * this. A clear privacy note explains the redaction guardrail.
 */
function AiSection() {
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [capRupees, setCapRupees] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .get<AiStatus>("/api/ai/status")
      .then((s) => {
        setStatus(s);
        setCapRupees(String(Math.round(s.capCents / 100)));
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load AI status"));
  };
  useEffect(load, []);

  const save = async (enabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const capCents = Math.max(1, Math.round(Number(capRupees) || 0)) * 100;
      await api.post("/api/ai/consent", { enabled, monthlyCostCapCents: capCents });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to save");
    } finally {
      setBusy(false);
    }
  };

  const spent = status ? status.spendCents / 100 : 0;
  const cap = status ? status.capCents / 100 : 0;

  return (
    <section>
      <h2>AI &amp; Automation ✨</h2>
      {error && <div className="error-text">{error}</div>}
      <div className="card">
        {!status ? (
          <p className="muted">Loading…</p>
        ) : !status.configured ? (
          <p className="muted">
            AI is not configured on the server yet (no API key). Contact your administrator.
          </p>
        ) : (
          <>
            <p>
              Status:{" "}
              {status.enabled ? (
                <strong style={{ color: "var(--ok, #128a4b)" }}>Enabled</strong>
              ) : (
                <span className="muted">Disabled</span>
              )}{" "}
              <span className="muted">
                · {status.provider} · model {status.model}
                {status.provider === "gemini" && ` · ${status.keyPoolSize} key${status.keyPoolSize === 1 ? "" : "s"} in pool`}
              </span>
            </p>
            <p className="muted" style={{ maxWidth: "42rem" }}>
              When enabled, the assistant auto-categorizes transactions, answers questions about
              your data, extracts documents and surfaces insights. Account numbers, PAN and phone
              numbers are masked before anything is sent to the AI, calls are logged, and your data
              is never used for training. You can turn this off any time.
            </p>
            <div className="toolbar" style={{ alignItems: "flex-end" }}>
              <div className="field">
                <label htmlFor="ai-cap">Monthly spend cap (₹)</label>
                <input
                  id="ai-cap"
                  type="number"
                  min={1}
                  value={capRupees}
                  onChange={(e) => setCapRupees(e.target.value)}
                  style={{ maxWidth: "8rem" }}
                />
              </div>
              {status.enabled ? (
                <>
                  <button type="button" disabled={busy} onClick={() => void save(true)}>
                    {busy ? "Saving…" : "Update cap"}
                  </button>
                  <button type="button" className="danger" disabled={busy} onClick={() => void save(false)}>
                    Disable AI
                  </button>
                </>
              ) : (
                <button className="primary" type="button" disabled={busy} onClick={() => void save(true)}>
                  {busy ? "Enabling…" : "Enable AI"}
                </button>
              )}
            </div>
            <p className="muted" style={{ marginTop: "0.5rem" }}>
              This month: {fmtINR(spent)} used of {fmtINR(cap)} cap.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

export function Settings() {
  const { user } = useAuth();

  // A tenant-less superadmin only manages tenants. A superadmin who also owns a
  // tenant (dual-capability) — and every normal owner — sees the tenant
  // settings, including AI; the superadmin additionally gets Tenants below.
  if (user?.role === "superadmin" && !user?.tenantId) {
    return (
      <div>
        <h1>Settings</h1>
        <TenantsSection />
      </div>
    );
  }

  return (
    <div>
      <h1>Settings</h1>
      <AiSection />
      <AccountsSection />
      <CategoriesSection />
      <RulesSection />
      <GmailSection />
      {user?.role === "superadmin" && <TenantsSection />}
    </div>
  );
}
